import "./App.css";
import "./Animations.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

import useSystem from "./customHooks/useSystem.js";
import { ToastContainer, Zoom } from "react-toastify";
import PageNotFoundView from "./views/utilitiesStack/PageNotFoundView";
import ErrorView from "./views/utilitiesStack/ErrorView.js";
import NavigationBar from "./components/Navbar.js";
import routes, { loginRoutes } from "./views/routes.js";
import NotVerifiedView from "./views/loginStack/NotVerifiedView/NotVerifiedView.js";
import ChangePasswordView from "./views/loginStack/External/ChangePasswordView.js";
import LoadingView from "./views/utilitiesStack/LoadingView";
import TokenExpiredView from "./views/utilitiesStack/TokenExpiredView.js";
import SuccessView from "./views/utilitiesStack/SuccessView.js";
import Background from "./assets/Background.png";
import useColors from "./customHooks/useColors";
import buildEprototypes from "./utility/Eproto";
import CookieView from "./views/utilitiesStack/CookieView";
import frontEndSettings from "./constants/frontEndSettings";
import Footer from "./components/Footer.js";
import { getApsoluteDomain } from "./utility/connect";
import packageJson from "../package.json";
import MessagesView from "./views/mainStack/messagesStack/MessagesView.js";

const isProduction = process.env.REACT_APP_ENV.toLowerCase() === "production";

buildEprototypes();

function App() {
  ////////////////////////////////////////////////use system

  const loginState = useSelector((state) => state.loginState);
  const globalErrorState = useSelector((state) => state.globalErrorState);
  const systemState = useSelector((state) => state.systemState);

  if (globalErrorState.isGlobalError) {
    return (
      <BrowserRouter>
        <ErrorView />
      </BrowserRouter>
    );
  }

  if (loginState.login.token.expired === true) {
    return (
      <BrowserRouter>
        <TokenExpiredView />
      </BrowserRouter>
    );
  }

  if (
    systemState.systemSettingsBucket.length > 0 ||
    systemState.systemSettingsFetchStarted === false
  ) {
    return <LoadingView />;
  }

  //not logged in routes
  const loginRoutesPath = loginRoutes.map((route) => route.path);
  const afterLoginRoutesPath = routes
    .map((route) => route.path)
    .filter((path) => path !== "/");

  if (loginState.login.isLoggedIn === false) {
    return (
      <BrowserRouter>
        <Routes>
          {/* login rute */}
          {loginRoutes.map((route) => (
            <Route
              key={route.name}
              element={
                <route.component
                  title={route.title}
                  afterLoginRoutesPath={afterLoginRoutesPath}
                />
              }
              path={route.path}
            />
          ))}
          <Route
            element={
              <PageNotFoundView
                afterLoginRoutesPath={afterLoginRoutesPath}
                loginRoutesPath={loginRoutesPath}
              />
            }
            path="*"
          />
        </Routes>
      </BrowserRouter>
    );
  }

  const loginMethod = loginState.login.loginMethod;
  const emailVerified = loginState.login.userInfo.isMailVerified;
  const phoneVerified = loginState.login.userInfo.isPhoneVerified;

  if (loginMethod === "EMAIL" && emailVerified === false) {
    return (
      <BrowserRouter>
        <NotVerifiedView EMAIL />
      </BrowserRouter>
    );
  }

  if (loginMethod === "MOBILE" && phoneVerified === false) {
    return (
      <BrowserRouter>
        <NotVerifiedView PHONE />
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <NavigationBar
        autoCollapseOnClick={
          frontEndSettings.bootstrapSettings.navbar.collapseOnClick
        }
      />
      <Routes>
        {loginRoutes
          .filter((route) => route.path !== "/")
          .map((route) => (
            <Route
              key={route.name}
              element={<route.component title={route.title} />}
              path={route.path}
              autoRelogin={route.autoRelogin}
            />
          ))}
        {routes.map((route) => (
          <Route
            key={route.name}
            element={<route.component />}
            path={route.path}
          />
        ))}
        <Route element={<ChangePasswordView />} path="changePassword" />
        <Route element={<MessagesView />} path="messagesPage" />
        <Route
          element={
            <PageNotFoundView
              afterLoginRoutesPath={afterLoginRoutesPath}
              loginRoutesPath={loginRoutesPath}
            />
          }
          path="*"
        />
      </Routes>
      {systemState.systemSettingsBucket.length > 0 && <LoadingView />}
    </BrowserRouter>
  );
}

const AppWraper = () => {
  const successState = useSelector((state) => state.successState);
  const systemState = useSelector((state) => state.systemState);

  const [currentColors] = useColors();
  const { cookiesAccepted, acceptCookies } = useSystem({ env: "WEB" });

  const styles = {
    appContainer: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      justifyContent: "stretch",
      backgroundImage: `url(${Background})`,
      backgroundColor: currentColors.primary,
      backgroundSize: "cover",
      flex: 1,
      border: `${isProduction ? "0px" : "3px"}  dotted red`,
    },
  };

  return (
    <>
      {isProduction === false && (
        <div>
          <span>{process.env.REACT_APP_ENV}</span>
          <span style={{ marginLeft: "10px" }}>
            {getApsoluteDomain() !== "/odisej"
              ? getApsoluteDomain()
              : `localhost (proxy ${packageJson.proxy})`}
          </span>
        </div>
      )}
      <div id="appContainer" style={styles.appContainer}>
        {systemState.systemSettingsBucket.length === 0 &&
          cookiesAccepted === false && (
            <CookieView
              isAccepted={cookiesAccepted}
              acceptCookies={acceptCookies}
            />
          )}
        <App />
        {successState.showSuccessBucket.length > 0 && <SuccessView />}
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          progress={undefined} //0-1
          pauseOnFocusLoss={false}
          draggable
          progressStyle={{ background: currentColors.primary }}
          toastStyle={{
            backgroundColor: currentColors.quinary,
            color: currentColors.primary,
          }}
          transition={Zoom}
        />
      </div>
      {systemState.systemSettingsBucket.length === 0 && <Footer />}
    </>
  );
};

export default AppWraper;
